const VERSION = '1.21.11'
const BASE = `https://assets.mcasset.cloud/${VERSION}/assets/minecraft/textures`
const RENDER_CHEST_BASE = normalizeAssetBase(
  (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env?.VITE_RENDER_CHEST_BASE_URL
    ?? '/renderchest',
)
const RENDER_CHEST_FORMAT =
  (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env?.VITE_RENDER_CHEST_FORMAT
    ?? 'webp'

export function bareId(id: string): string {
  return id.replace(/^minecraft:/, '')
}

export function namespacedId(id: string): { namespace: string; path: string } {
  const [namespace, ...path] = id.includes(':') ? id.split(':') : ['minecraft', id]
  return {
    namespace: namespace || 'minecraft',
    path: path.join(':') || id,
  }
}

function normalizeAssetBase(value: string): string {
  return value.replace(/\/+$/, '')
}

export function renderChestIconUrl(id: string): string {
  const { namespace, path } = namespacedId(id)
  return `${RENDER_CHEST_BASE}/items/${encodeURIComponent(namespace)}/${path.split('/').map(encodeURIComponent).join('/')}.${RENDER_CHEST_FORMAT}`
}

export function itemIconUrl(id: string): string {
  return `${BASE}/item/${bareId(id)}.png`
}

export function blockIconUrl(id: string): string {
  return `${BASE}/block/${bareId(id)}.png`
}

// Mojang's raw assets only ship a flat PNG for full-cube blocks (e.g. block/dirt.png).
// Slabs / stairs / walls / fences / doors etc. are rendered into a 3D cube at runtime from
// a base face texture, so mcasset.cloud has no file at that id. Derive the closest face
// texture from the id so the panel shows *something* recognisable instead of a text fallback.
const WOOD_MATERIALS = new Set([
  'oak', 'spruce', 'birch', 'jungle', 'acacia', 'dark_oak',
  'mangrove', 'cherry', 'pale_oak', 'bamboo',
  'crimson', 'warped',
])

const DERIVED_SUFFIXES = [
  '_hanging_sign', '_pressure_plate', '_fence_gate', '_trapdoor',
  '_stairs', '_fence', '_button', '_door', '_sign', '_slab', '_wall',
]

export function derivedBlockIconUrl(id: string): string | null {
  const bare = bareId(id)
  for (const suffix of DERIVED_SUFFIXES) {
    if (bare.endsWith(suffix) && bare.length > suffix.length) {
      const base = bare.slice(0, -suffix.length)
      const texture = WOOD_MATERIALS.has(base) ? `${base}_planks` : base
      return `${BASE}/block/${texture}.png`
    }
  }
  return null
}

// Bare id → texture (and optional crop) under `${BASE}/`. Tried before item/<id>
// and block/<id>. A bare string picks one whole face from a multi-face block.
// The object form crops a UV-atlas texture to a single face (e.g. shield):
//   - `src` is the texture path (no extension)
//   - `crop` is [x, y, w, h] in source pixels of the region to show
//   - `atlas` is the full [w, h] of the source PNG
type IconOverride =
  | string
  | { src: string; crop: [number, number, number, number]; atlas: [number, number] }

const TEXTURE_OVERRIDES: Record<string, IconOverride> = {
  composter: 'block/composter_top',
  furnace: 'block/furnace_front',
  blast_furnace: 'block/blast_furnace_front',
  smoker: 'block/smoker_front',
  dispenser: 'block/dispenser_front',
  dropper: 'block/dropper_front',
  observer: 'block/observer_front',
  lectern: 'block/lectern_front',
  jukebox: 'block/jukebox_top',
  beehive: 'block/beehive_front',
  bee_nest: 'block/bee_nest_front',
  crafter: 'block/crafter_north',
  barrel: 'block/barrel_top',
  loom: 'block/loom_top',
  cartography_table: 'block/cartography_table_top',
  fletching_table: 'block/fletching_table_top',
  smithing_table: 'block/smithing_table_top',
  chiseled_bookshelf: 'block/chiseled_bookshelf_top',
  decorated_pot: 'block/decorated_pot_side',
  bell: 'block/bell_bottom',
  grindstone: 'block/grindstone_pivot',
  stonecutter: 'block/stonecutter_top',
  enchanting_table: 'block/enchanting_table_top',
  brewing_stand: 'block/brewing_stand_base',
  piston: 'block/piston_side',
  sticky_piston: 'block/piston_side',
  tipped_arrow: 'item/tipped_arrow_head',
  // Shield ships only as a UV atlas (front + back + edges + handle). The body
  // box is unwrapped at offset (D, D) where D = 1 (depth), so the front face is
  // at (1, 1)-(13, 23) — 12×22 — not at the texture origin.
  shield: { src: 'entity/shield_base_nopattern', crop: [1, 1, 12, 22], atlas: [64, 64] },
}

export interface IconOverrideResolved {
  url: string
  crop?: [number, number, number, number]
  atlas?: [number, number]
}

export function overrideIcon(id: string): IconOverrideResolved | null {
  const entry = TEXTURE_OVERRIDES[bareId(id)]
  if (entry == null) return null
  if (typeof entry === 'string') return { url: `${BASE}/${entry}.png` }
  return { url: `${BASE}/${entry.src}.png`, crop: entry.crop, atlas: entry.atlas }
}

const EFFECT_LABELS: Record<string, string> = {
  'minecraft:speed': 'Speed',
  'minecraft:slowness': 'Slowness',
  'minecraft:haste': 'Haste',
  'minecraft:mining_fatigue': 'Mining Fatigue',
  'minecraft:strength': 'Strength',
  'minecraft:instant_health': 'Instant Health',
  'minecraft:instant_damage': 'Instant Damage',
  'minecraft:jump_boost': 'Jump Boost',
  'minecraft:nausea': 'Nausea',
  'minecraft:regeneration': 'Regeneration',
  'minecraft:resistance': 'Resistance',
  'minecraft:fire_resistance': 'Fire Resistance',
  'minecraft:water_breathing': 'Water Breathing',
  'minecraft:invisibility': 'Invisibility',
  'minecraft:blindness': 'Blindness',
  'minecraft:night_vision': 'Night Vision',
  'minecraft:hunger': 'Hunger',
  'minecraft:weakness': 'Weakness',
  'minecraft:poison': 'Poison',
  'minecraft:wither': 'Wither',
  'minecraft:health_boost': 'Health Boost',
  'minecraft:absorption': 'Absorption',
  'minecraft:saturation': 'Saturation',
  'minecraft:glowing': 'Glowing',
  'minecraft:levitation': 'Levitation',
  'minecraft:luck': 'Luck',
  'minecraft:unluck': 'Bad Luck',
  'minecraft:slow_falling': 'Slow Falling',
  'minecraft:conduit_power': 'Conduit Power',
  'minecraft:dolphins_grace': "Dolphin's Grace",
  'minecraft:bad_omen': 'Bad Omen',
  'minecraft:hero_of_the_village': 'Hero of the Village',
  'minecraft:darkness': 'Darkness',
}

export function effectLabel(id: string): string {
  return EFFECT_LABELS[id] ?? prettyId(id)
}

const ENCHANTMENT_LABELS: Record<string, string> = {
  'aqua_affinity': 'Aqua Affinity',
  'bane_of_arthropods': 'Bane of Arthropods',
  'binding_curse': 'Curse of Binding',
  'blast_protection': 'Blast Protection',
  'breach': 'Breach',
  'channeling': 'Channeling',
  'density': 'Density',
  'depth_strider': 'Depth Strider',
  'efficiency': 'Efficiency',
  'feather_falling': 'Feather Falling',
  'fire_aspect': 'Fire Aspect',
  'fire_protection': 'Fire Protection',
  'flame': 'Flame',
  'fortune': 'Fortune',
  'frost_walker': 'Frost Walker',
  'impaling': 'Impaling',
  'infinity': 'Infinity',
  'knockback': 'Knockback',
  'looting': 'Looting',
  'loyalty': 'Loyalty',
  'luck_of_the_sea': 'Luck of the Sea',
  'lure': 'Lure',
  'mending': 'Mending',
  'multishot': 'Multishot',
  'piercing': 'Piercing',
  'power': 'Power',
  'projectile_protection': 'Projectile Protection',
  'protection': 'Protection',
  'punch': 'Punch',
  'quick_charge': 'Quick Charge',
  'respiration': 'Respiration',
  'riptide': 'Riptide',
  'sharpness': 'Sharpness',
  'silk_touch': 'Silk Touch',
  'smite': 'Smite',
  'soul_speed': 'Soul Speed',
  'sweeping_edge': 'Sweeping Edge',
  'swift_sneak': 'Swift Sneak',
  'thorns': 'Thorns',
  'unbreaking': 'Unbreaking',
  'vanishing_curse': 'Curse of Vanishing',
  'wind_burst': 'Wind Burst',
}

export function enchantmentLabel(id: string): string {
  return ENCHANTMENT_LABELS[bareId(id)] ?? prettyId(id)
}

export function parseEnchantment(s: string): { id: string; level: number } {
  const trimmed = s.trim()
  const lastSpace = trimmed.lastIndexOf(' ')
  if (lastSpace < 0) return { id: trimmed, level: 1 }
  const idPart = trimmed.slice(0, lastSpace).trim()
  const lvl = Number.parseInt(trimmed.slice(lastSpace + 1).trim(), 10)
  if (Number.isNaN(lvl)) return { id: trimmed, level: 1 }
  return { id: idPart, level: lvl }
}

export function prettyId(id: string): string {
  return bareId(id)
    .split('_')
    .map(p => (p[0]?.toUpperCase() ?? '') + p.slice(1))
    .join(' ')
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X']
export function toRoman(n: number): string {
  if (n < 0) return String(n + 1)
  if (n < ROMAN.length) return ROMAN[n]
  return String(n + 1)
}

export function ticksToClock(ticks: number): string {
  if (!Number.isFinite(ticks) || ticks <= 0) return '0:00'
  const totalSec = Math.floor(ticks / 20)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}
